export type UsHoliday = {
  date: Date;
  name: string;
  observed: boolean;
};

const SUNDAY = 0;
const MONDAY = 1;
const THURSDAY = 4;
const SATURDAY = 6;

const nthWeekday = (year: number, month: number, weekday: number, n: number): Date => {
  const first = new Date(year, month, 1);
  const offset = (weekday - first.getDay() + 7) % 7;
  return new Date(year, month, 1 + offset + (n - 1) * 7);
};

const lastWeekday = (year: number, month: number, weekday: number): Date => {
  const last = new Date(year, month + 1, 0);
  const offset = (last.getDay() - weekday + 7) % 7;
  return new Date(year, month, last.getDate() - offset);
};

const fixed = (date: Date, name: string): UsHoliday => {
  const shift = date.getDay() === SATURDAY ? -1 : date.getDay() === SUNDAY ? 1 : 0;
  if (shift === 0) return { date, name, observed: false };
  return {
    date: new Date(date.getFullYear(), date.getMonth(), date.getDate() + shift),
    name,
    observed: true,
  };
};

const floating = (date: Date, name: string): UsHoliday => ({ date, name, observed: false });

const holidaysOf = (year: number): UsHoliday[] => [
  fixed(new Date(year, 0, 1), "New Year's Day"),
  floating(nthWeekday(year, 0, MONDAY, 3), 'Martin Luther King Jr. Day'),
  floating(nthWeekday(year, 1, MONDAY, 3), "Presidents' Day"),
  floating(lastWeekday(year, 4, MONDAY), 'Memorial Day'),
  ...(year >= 2021 ? [fixed(new Date(year, 5, 19), 'Juneteenth National Independence Day')] : []),
  fixed(new Date(year, 6, 4), 'Independence Day'),
  floating(nthWeekday(year, 8, MONDAY, 1), 'Labor Day'),
  floating(nthWeekday(year, 9, MONDAY, 2), 'Columbus Day'),
  fixed(new Date(year, 10, 11), 'Veterans Day'),
  floating(nthWeekday(year, 10, THURSDAY, 4), 'Thanksgiving Day'),
  fixed(new Date(year, 11, 25), 'Christmas Day'),
];

export const getUsHolidays = (year: number): UsHoliday[] =>
  [...holidaysOf(year), fixed(new Date(year + 1, 0, 1), "New Year's Day")]
    .filter((holiday) => holiday.date.getFullYear() === year)
    .sort((a, b) => a.date.getTime() - b.date.getTime());

const dateKey = (date: Date) => `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;

const byYear = new Map<number, Map<string, UsHoliday>>();

export const getUsHoliday = (date: Date): UsHoliday | undefined => {
  const year = date.getFullYear();
  let holidays = byYear.get(year);
  if (!holidays) {
    holidays = new Map(getUsHolidays(year).map((holiday) => [dateKey(holiday.date), holiday]));
    byYear.set(year, holidays);
  }
  return holidays.get(dateKey(date));
};
